import { useEffect } from 'react';
import { isDevelopment } from '../utils/environment';
import {
  initializeFirebaseAnalytics,
  trackEventAsync,
  getAnalyticsStatus,
  setAnalyticsUserProperties,
  subscribeGlobalButtonClickAnalytics,
} from '../services/firebaseAnalytics';
import { ENV } from '../config/env';

/**
 * Custom hook for Firebase Analytics lifecycle on mount.
 *
 * In one effect: starts async initialization, registers delegated `button_click` tracking,
 * and tears down the listener on unmount. Keeps ordering and cleanup in a single place.
 *
 * Note: Firebase Analytics service is imported at module level for faster initialization.
 */
export const useFirebaseAnalytics = () => {
  useEffect(() => {
    const initializeAnalytics = async () => {
      try {
        // Always expose debug helpers in development so we can inspect init failures too.
        if (isDevelopment && typeof window !== 'undefined') {
          window.__firebaseAnalytics = {
            trackEvent: trackEventAsync,
            getStatus: getAnalyticsStatus,
            testEvent: () => trackEventAsync('test_event', { test: true, timestamp: Date.now() }),
          };
        }

        const analyticsInstance = await initializeFirebaseAnalytics();

        if (analyticsInstance) {
          // Set baseline properties for all sessions (anonymous or signed-in).
          // Keep this lightweight; anything user-specific should be set after login.
          await setAnalyticsUserProperties({
            environment: ENV.VITE_ENV,
            app_version: ENV.VITE_APP_VERSION || null,
            auth_provider: ENV.VITE_AUTH_PROVIDER || null,
            hostname: typeof window !== 'undefined' ? window.location.hostname : null,
            platform: 'web',
          });

          // Track app launch event (fire-and-forget to avoid timing issues)
          trackEventAsync('app_launched', {
            timestamp: new Date().toISOString(),
            platform: 'web',
          });
        } else {
          if (isDevelopment) {
            console.warn('⚠️ Firebase Analytics initialization returned null.');
          }
        }
      } catch (error) {
        if (isDevelopment) {
          console.error('❌ Failed to initialize or track with Firebase Analytics:', error);
          console.error('Error details:', error);
        }
      }
    };

    void initializeAnalytics();
    const unsubscribeButtonClicks = subscribeGlobalButtonClickAnalytics();

    return () => {
      unsubscribeButtonClicks();
    };
  }, []);
};
